var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.platform",
  title: "EGT Platform page",
  description: "The Universe Platform micro-site hub — one language, seen many times (why + what, never how).",
  supportServer: true,
  fields: { reserveUrl: { title: "Reserve URL", type: "uri", description: "Reservation entry point.", required: false } }
});
var S = require("../helpers/egt.sections.js");
var P = require("../helpers/egt.platform.sections.js");

exports.render = async function render(options) {
  var reserve = (options && options.reserveUrl) || "/reserve";

  /* Per-render footnote system (server-renderable; NO shared/global state, so
   * numbering is request-scoped). Inline "Illustrative — sample data…" notes
   * become numbered markers on their section titles + one list at the bottom.
   * See S.footnotes in app/helpers/egt.sections.js. */
  var FN = S.footnotes();
  /* One canonical illustrative note (WP-N6.8): the interactive demos on this
   * page all run on invented sample data. Using the SAME text on each mark lets
   * S.footnotes() dedupe them to a single number. Honest + no dates (§4). */
  var ILLUSTRATIVE = "An illustrative demo running on invented sample data — the names, prices, and records shown aren't real, nothing here is a live account, and nothing is saved.";

  /* All platform sections live inside a single content root (.egt-platform).
   * "Make it yours" sets --egt-gold / --egt-gold-rgb / data-pf-font /
   * data-pf-cat inline on THIS node so the brand cascades across every
   * platform section at once — but NOT onto the shared nav/footer, which
   * S.page() renders as siblings OUTSIDE this wrapper (keeps the global
   * chrome on the house palette, per the spec). */
  var sections = [
    /* 1 — HERO over the stallion footage; a TYPING headline cycles the verbs,
     * then a long honest "everything it does" run, ending "That was easy." */
    P.hero({
      media: { video: "/vis/stallion.mp4", poster: "/vis/stallion.jpg", width: "1920", height: "1080", parallax: 0.06 },
      eyebrow: "The Universe Platform by EGT",
      title: [
        { n: "span", c: "egt-pf-type egt__gold", "data-pf-type": "Create|Sell|Share|Grow|Automate|Scale", i: "Create" },
        { n: "span", c: "egt-pf-type__caret", "aria-hidden": "true" },
        " ", { n: "br" }, "without limits."
      ],
      lead: "Take a breath. Pages, blogs, e‑commerce, email, rewards, pickup in store, subscriptions — physical and digital — automation, campaigns, paid content, memberships, downloads, point of sale, and a great deal more. That was easy.",
      cta: S.cta(reserve, "Join Beta", true)
    }),

    /* 1b — CAPTURE (F22). Straight after the hero, before the product story:
     * catch the address while the promise is fresh. Light on copy (founder
     * ruling F22) — an eyebrow, one line, the inline field. source "platform"
     * → the platform newsletter (egt.capture.js SOURCES). Frosted panel over
     * the page background, undimmed (agents.md §4 — no scrim). Consent tick
     * REQUIRED + never pre-checked, same as home/voice/album. */
    {
      n: "section", c: "egt-section egt-section--center", id: "egt-platform-capture",
      i: { n: "div", c: "egt__shell egt-reveal",
        i: S.capture({
          source: "platform",
          id: "egt-cap-platform",
          variant: "panel",
          eyebrow: "The Universe Platform",
          heading: "h2",
          title: [ "Be first ", { n: "span", c: "egt__gold", i: "in." } ],
          lead: "Watch the platform take shape — reserve your spot and we'll tell you the moment it opens.",
          label: "Your email address",
          cta: "Notify me",
          consent: true
        }) }
    },

    /* 2 — the thesis line: a deliberate typographic beat, not a small caption.
     * Platform-scoped class (.egt-pf-statement) so the shared S.statement /
     * .egt-lead styling stays untouched on every other page. Fills ≥ half the
     * viewport; big, confident Playfair; gold used once (the aspiration). */
    {
      n: "section", c: "egt-section egt-section--center egt-pf-statement",
      i: { n: "div", c: "egt__shell egt-reveal",
        i: { n: "blockquote", c: "egt-pf-statement__line",
          i: [
            "You shouldn't have to ",
            { n: "span", c: "egt-pf-statement__em", i: "become an engineer" },
            " to ",
            { n: "span", c: "egt-pf-statement__gold", i: "exist online." }
          ] } }
    },

    /* 3 — UNIVERSE SEARCH: the interactive product/search demo (one index) */
    P.search(FN.mark(ILLUSTRATIVE)),

    /* 4 — THE LENS: one sample object, re-composed through six surfaces */
    P.lens(FN.mark(ILLUSTRATIVE)),

    /* 4b — confidence beat: the site itself is the proof */
    S.quote([ "We believe in the platform. ", { n: "span", c: "egt__gold", i: "We built this on it's key components." } ]),

    /* 5 — REAL PRODUCT, IN A FRAME: device-tab switch over real screenshots */
    P.productFrame(FN.mark("Genuine product screens, populated with sample content. The platform is in active development and opens first to public-beta members.")),

    /* 6 — MAKE IT YOURS: live brand controls re-skin a sample card */
    P.makeItYours(),

    /* 6b — dedicated full heroes for the big stories (founder: full-frame heros
     * above the bento; Subscriptions closer to the top). Frosted glass over
     * fresh imagery (swappable via the media manager), undimmed media. */
    S.canvas({
      media: { img: "/imgs/pf/pf-subscriptions.jpg", alt: "", parallax: true },
      align: "center", width: "wide", tall: true, c: "egt-pf-fhero",
      content: [
        { n: "p", c: "egt__eyebrow", i: "Subscribe & Go" },
        { n: "h2", c: "egt-section__title", i: "Recurring revenue, your way." },
        { n: "p", c: "egt-section__lead", i: "Take your business to the next level — memberships for fans, gated content, digital and physical subscriptions. Everything the creator platforms promise, on a brand and a customer list you own." },
        { n: "div", c: "egt-phero__actions", i: S.cta(reserve, "Join Beta", true) }
      ]
    }),
    /* 7b — MARKETPLACE preview: relocated into the slot the Discovery hero
     * vacated (founder: move Marketplace to where Discovery was). Single
     * instance; content rebuild is a later package — moved intact. */
    P.shop(),
    S.canvas({
      media: { img: "/imgs/pf/pf-team.jpg", alt: "", parallax: true },
      align: "center", width: "wide", tall: true, c: "egt-pf-fhero",
      content: [
        { n: "p", c: "egt__eyebrow", i: "Your team" },
        { n: "h2", c: "egt-section__title", i: "Run the whole process, together." },
        { n: "p", c: "egt-section__lead", i: "Rich, role-based access that lets your team go beyond making content to walking your entire process — sharing what they should, managing what they own." }
      ]
    }),
    /* 6c — YOUR TEAM interactive demo (WP-6a): role-based access preview + a
     * role-scoped invite composer, as one team console. Grounds the fhero's
     * "role-based access" promise in something you can drive. */
    P.team(),
    S.canvas({
      media: { img: "/imgs/pf/pf-wizards.jpg", alt: "", parallax: true },
      align: "center", width: "wide", tall: true, c: "egt-pf-fhero",
      content: [
        { n: "p", c: "egt__eyebrow", i: "Wizards" },
        { n: "h2", c: "egt-section__title", i: "Collect everything." },
        { n: "p", c: "egt-section__lead", i: "Gorgeous multistep forms that are magically easy to create — and couple with workflows for automation that runs itself." }
      ]
    }),
    /* 6d — WIZARDS interactive demo (WP-6b): a driveable multistep wizard whose
     * submission lands in a live dashboard (collect everything → see
     * everything), plus an honest note that Join Beta + the Forge intake are
     * built on this same wizard engine. */
    P.wizards(),

    /* 6e — SAME ENGINE (WP-N6.5): the "you've already used one" proof, pulled
     * out of the wizards section into its OWN deliberate half-viewport beat —
     * generous space, strong typography, the two wizards the visitor has
     * already touched (reserve + Forge) linked as evidence. */
    P.sameEngine(),

    /* 7 — BENTO: "what's inside" breadth-at-a-glance, grounded in real caps */
    P.bento(),

    /* 8 — capability routing: six doors (Live/Coming chips removed per 3.6) */
    S.section({
      eyebrow: "One platform",
      title: "Everything belongs together.",
      lead: "Not a pile of apps that each take a cut and break when you touch them — one system, six views, speaking a single language. Open any door.",
      body: [ P.capabilityGrid() ]
    }),

    /* 9 — proof-of-depth: a quiet, honest capability ledger */
    P.depth(),

    /* 10 — ownership beat: hand-built canvas (vs S.canvas) over a SINGLE
     * undimmed media layer (egt-platform-canvas.jpg). The old P.catBackdrop()
     * second layer was removed (WP-N6.6): it double-stacked backgrounds and
     * surfaced the category-mood imagery (incl. the ceramics bread-bowl) the
     * founder doesn't want here. Contrast from the frosted glass only. */
    {
      n: "section", c: "egt-section egt-canvas egt-canvas--center egt-canvas--tall egt-pf-own",
      i: [
        S.media({ img: "/imgs/egt-platform-canvas.jpg", alt: "", parallax: true }),
        { n: "div", c: "egt__shell egt-canvas__inner",
          i: S.glass({ width: "wide", content: [
            { n: "p", c: "egt__eyebrow", i: "Ownership" },
            { n: "h2", c: "egt-section__title", i: "Stop renting your business back from your tools." },
            { n: "p", c: "egt-section__lead", i: "Your site, your customers, your store, your data — held in one place you own, not scattered across a dozen rentals." },
            { n: "div", c: "egt-phero__actions", i: S.cta("/platform/compare/the-stack", "See the difference") }
          ] }) }
      ]
    },

    /* 11 — reserve crescendo (WP-N6.7): THE closing CTA, given full-page-height
     * weight — a proper crescendo, not an afterthought strip. Full-viewport
     * section over the stallion footage (bookends the hero), one big frosted
     * card, confident Playfair, scarce gold, honest posture (no dates). The CTA
     * is a plain link to /reserve (the wizard lives there now — G-7/N2). */
    {
      n: "section", c: "egt-section egt-canvas egt-canvas--center egt-canvas--tall egt-pf-reserve",
      i: [
        S.media({ video: "/vis/stallion.mp4", poster: "/vis/stallion.jpg", width: "1920", height: "1080", parallax: 0.05 }),
        { n: "div", c: "egt__shell egt-canvas__inner",
          i: S.glass({ width: "wide", c: "egt-pf-reserve__card egt-reveal", content: [
            { n: "p", c: "egt__eyebrow", i: "Founding reservation" },
            { n: "h2", c: "egt-pf-reserve__title", i: [ "Claim your ", { n: "span", c: "egt__gold", i: "founding place." } ] },
            { n: "p", c: "egt-pf-reserve__lead", i: "Join the public Beta for $9.57 — a non-refundable reservation on your founding spot, credited in full toward your launch, and provisioned first." },
            { n: "ul", c: "egt-pf-reserve__points", "aria-label": "What your reservation holds",
              i: [
                { n: "li", c: "egt-pf-reserve__point", i: [ { n: "span", c: "egt-pf-reserve__point-k", i: "Held for you" }, "Your founding place, kept the moment you reserve." ] },
                { n: "li", c: "egt-pf-reserve__point", i: [ { n: "span", c: "egt-pf-reserve__point-k", i: "Non-refundable, credited" }, "The full $9.57 applies to your launch — nothing lost." ] },
                { n: "li", c: "egt-pf-reserve__point", i: [ { n: "span", c: "egt-pf-reserve__point-k", i: "First in line" }, "Provisioned ahead of everyone else." ] }
              ] },
            { n: "div", c: "egt-phero__actions egt-pf-reserve__actions", i: S.cta(reserve, "Reserve your spot", true) },
            { n: "p", c: "egt-pf-reserve__fine", i: "Your founding place, held for you." }
          ] }) }
      ]
    },

    /* 12 — FOOTNOTES: the numbered footnote list, rendered once at the page bottom
     * (above the footer). Empty <div> if no markers were made. All FN.mark()
     * calls above run during this array's construction, so every note is here. */
    FN.list()
  ];

  /* the platform content root — the brand target for the live controls */
  var root = {
    n: "div", c: "egt-platform", id: "egt-platform-root",
    "data-pf-font": "serif", "data-pf-cat": "ceramics",
    i: sections
  };

  return S.page(reserve, [ platformSchema(), root ], { betaCta: true, footerCaptureLabel: S.FOOTER_LABELS.platform });
};

/* Per-surface JSON-LD: WebPage + BreadcrumbList + SoftwareApplication for the
 * Platform surface. Site-wide Organization + WebSite come from S.page. URLs are
 * built from S.siteOrigin() (follows the production domain). The
 * SoftwareApplication description is drawn from on-page copy; NO offers or
 * aggregateRating — the platform is a public beta with no public pricing or
 * reviews (no fabricated facts). */
function platformSchema() {
  var O = S.siteOrigin();
  return S.jsonLd([
    { "@type": "WebPage", "@id": O + "/platform#webpage", url: O + "/platform",
      name: "The Universe Platform",
      isPartOf: { "@id": O + "/#website" },
      about: { "@id": O + "/platform#app" },
      breadcrumb: { "@id": O + "/platform#breadcrumb" } },
    { "@type": "BreadcrumbList", "@id": O + "/platform#breadcrumb",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: O + "/" },
        { "@type": "ListItem", position: 2, name: "Platform" }
      ] },
    { "@type": "SoftwareApplication", "@id": O + "/platform#app",
      name: "The Universe Platform",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      description: "Run your whole business from one place — pages, blogs, e-commerce, email, rewards, subscriptions, memberships, point of sale, and automation, all under your own brand.",
      publisher: { "@id": O + "/#organization" } }
  ]);
}
